import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { PaymentView } from "../components/payment-view";
import { BasketView } from "../components/basket-view";
import { DeliveryView } from "../components/delivery-view";
import { ConfirmationView } from "../components/confirmation-view";
import { CheckoutLockedView } from "../components/checkout-locked-view";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { useCheckoutStore } from "../../domain/useCheckoutStore";
import { useApprovalStatus } from "../../../../hooks/useApprovalStatus";

const STEPS = ["basket", "delivery", "payment", "confirmation"] as const;
type Step = (typeof STEPS)[number];

function isStep(value: string | null): value is Step {
  return value != null && (STEPS as readonly string[]).includes(value);
}

/**
 * Stepper-based checkout host. Step lives in the URL (`?step=basket|delivery|
 * payment|confirmation`) so refresh / back / share / deep-link from a
 * "Buy now" button all land on the right view.
 *
 * The cart-driven path runs through every step; the existing-quotation
 * path (?saleId=…) defaults to `payment` because basket / delivery were
 * captured when the quotation was created.
 *
 * Real Stripe integration lives in PaymentView. After successful payment,
 * the resulting saleId is threaded into ConfirmationView via the URL
 * (`?step=confirmation&saleId=…`), which fetches the sale from the BE so
 * the order number / address / totals match what was actually persisted.
 */
export function CheckoutPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { items } = useCartStore();
  const { setCheckoutItems, shippingAddressId } = useCheckoutStore();
  const { status: approvalStatus, isLoading: isLoadingApproval } = useApprovalStatus();

  const saleIdParam = searchParams.get("saleId");
  const incomingSaleId = saleIdParam ? Number(saleIdParam) : null;
  const validIncomingSaleId =
    incomingSaleId != null && Number.isFinite(incomingSaleId) ? incomingSaleId : null;

  const stepParam = searchParams.get("step");
  const step: Step = isStep(stepParam)
    ? stepParam
    : validIncomingSaleId
      ? "payment"
      : "basket";

  useEffect(() => {
    if (!validIncomingSaleId) return;
    if (stepParam === "payment" || stepParam === "confirmation") return;
    const params = new URLSearchParams(searchParams);
    params.set("step", "payment");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, stepParam, validIncomingSaleId]);

  // The confirmation view reads its own saleId from the URL too — so a
  // refresh after payment still resolves to the right order. We keep a
  // local mirror just to detect the moment of transition (so we can avoid
  // bouncing to /products when the cart is empty post-payment).
  const [hasJustConfirmed, setHasJustConfirmed] = useState(false);

  const goToStep = useCallback(
    (next: Step, extra?: { saleId?: number }) => {
      const params = new URLSearchParams(searchParams);
      params.set("step", next);
      if (extra?.saleId != null) params.set("saleId", String(extra.saleId));
      if (next === "payment" && !validIncomingSaleId) {
        setCheckoutItems(items);
      }
      setSearchParams(params, { replace: false });
    },
    [items, searchParams, setCheckoutItems, setSearchParams, validIncomingSaleId],
  );

  const handlePaymentSuccess = (saleId: number) => {
    setHasJustConfirmed(true);
    goToStep("confirmation", { saleId });
  };

  useEffect(() => {
    // If the cart is empty and we're not in a post-payment state, send the
    // user back to browse — there is nothing to check out.
    // Skip the redirect on "payment" step when a saleId exists (the payment
    // overlay may still be visible while the cart is being cleared).
    if (
      !validIncomingSaleId &&
      items.length === 0 &&
      step !== "confirmation" &&
      step !== "payment" &&
      !hasJustConfirmed
    ) {
      navigate(AppRoutes.PRODUCTS);
    }
  }, [hasJustConfirmed, items.length, navigate, step, validIncomingSaleId]);

  // The cart path creates the order as soon as the payment step mounts, so it
  // must not get there without a delivery address — which is exactly what a
  // refresh or a deep link to `?step=payment` does (the selection lives in
  // memory). Send the buyer back to pick one.
  useEffect(() => {
    if (step !== "payment" || validIncomingSaleId || shippingAddressId != null) return;
    if (hasJustConfirmed) return;
    const params = new URLSearchParams(searchParams);
    params.set("step", "delivery");
    setSearchParams(params, { replace: true });
  }, [hasJustConfirmed, searchParams, setSearchParams, shippingAddressId, step, validIncomingSaleId]);

  // Wait for the approval status before rendering any step: while it loads
  // the hook reports "approved", and PaymentView creates the draft order on
  // mount. A buyer whose prices are hidden can't place an order (BE refuses
  // the payment too), so every step — including a deep link — shows the lock.
  if (isLoadingApproval) {
    return (
      <div className="flex w-full flex-col px-8 py-6">
        <ShimmerLoader message="Checking your account before checkout…" />
      </div>
    );
  }

  if (approvalStatus !== "approved") {
    return <CheckoutLockedView />;
  }

  if (step === "basket") {
    return <BasketView onProceed={() => goToStep("delivery")} />;
  }

  if (step === "delivery") {
    return <DeliveryView onProceed={() => goToStep("payment")} />;
  }

  if (step === "payment") {
    if (!validIncomingSaleId && shippingAddressId == null) return null;
    return (
      <PaymentView
        saleId={validIncomingSaleId}
        onSuccess={handlePaymentSuccess}
        onChangeDeliveryAddress={() => goToStep("delivery")}
      />
    );
  }

  if (step === "confirmation") {
    // Confirmation reads `?saleId=…` straight from the URL so the page is
    // shareable and refresh-safe.
    if (validIncomingSaleId == null) {
      navigate(AppRoutes.PRODUCTS, { replace: true });
      return null;
    }
    return (
      <ConfirmationView
        saleId={validIncomingSaleId}
        onContinueShopping={() => navigate(AppRoutes.PRODUCTS)}
      />
    );
  }

  return null;
}
